import React from "react";
import { MultipleDynamicsFilesInput } from "../../../types";
import {
  Box,
  FormControl,
  FormHelperText,
  FormLabel,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { Delete, FileUploadOutlined } from "@mui/icons-material";
import ClickableIcon from "../../common/buttons/icons/ClickableIcon";
import { defaultMaxSize, defaultAcceptedExtensions } from "./filesSettings";
import clone from "just-clone";

type Props = {
  field: MultipleDynamicsFilesInput;
  fieldName: string;
  handleChange: (field: MultipleDynamicsFilesInput) => void;
  disabled: boolean;
};

const FormMultipleDynamicsFileInput = ({
  field,
  fieldName,
  handleChange,
  disabled,
}: Props) => {
  function handleInputChange(event: React.ChangeEvent<HTMLInputElement>) {
    const files = event.target.files;
    const maxSize = field.maxSize ? field.maxSize : defaultMaxSize;
    const accept = field.accept ? field.accept : defaultAcceptedExtensions;
    let _field = { ...field };
    _field.error = "";
    const _oldFiles = [..._field.value];
    if (files !== null) {
      if (field.maxLength === 1) {
        const file = files.item(0);
        if (file) {
          if (file.size > maxSize) {
            _field.error =
              "Le fichier doit avoir une taille inférieure à " +
              Math.round(maxSize / (1024 * 1024)) +
              " Mo";
          } else {
            _oldFiles[0] = { file: file };
            _field.error = "";
            _field.changed = true;
          }
        }
      } else {
        for (var i = 0; i < files.length; i++) {
          const file = files.item(i);
          if (!file) continue;
          if (file.size > maxSize) {
            _field.error =
              "Le fichier doit avoir une taille inférieure à " +
              Math.round(maxSize / (1024 * 1024)) +
              " Mo";
            continue;
          }

          if (
            !accept.some((ext) =>
              file.name.toLowerCase().endsWith(ext.toLowerCase())
            )
          ) {
            _field.error =
              "Les extensions acceptées sont: " + accept.join(", ");
            continue;
          }

          if (_oldFiles.filter((val) => !("delete" in val)).length >= field.maxLength) {
            _field.error = "Nombre maximum de fichiers atteint";
            break;
          }

          var found = false;
          for (var a = 0; a < _oldFiles.length; a++) {
            const _oldFile = _oldFiles[a];
            if ("delete" in _oldFile) continue;
            const name =
              "name" in _oldFile ? _oldFile.name : _oldFile.file.name;
            if (file.name === name) {
              found = true;
              break;
            }
          }

          if (found) {
            
            _field.error = "Les fichiers doivent être uniques";
            continue;
          }
          _oldFiles.push({ file: file });
          _field.changed = true;
        }
      }
    }
    _field.value = _oldFiles;
    handleChange(_field);
  }

  function handleDelete(idx: number) {
    let _field = { ...field };
    const toDelete = _field.value[idx];
    let newValue = clone(_field.value);
    if ("id" in toDelete) {
      newValue[idx] = { ...toDelete, delete: true };
    } else {
      newValue = newValue.filter((obj, id) => id !== idx);
    }

    _field.value = newValue;
    handleChange(_field);
  }

  return (
    <Box width="100%">
      <TextField
        value={""}
        name={fieldName}
        required={field.required}
        error={!(field.error === "")}
        helperText={field.error ? field.error : field.helperText}
        disabled={disabled}
        fullWidth
        label={field.label}
        InputProps={{
          sx: { cursor: "pointer", pointerEvents: "none" },
          endAdornment: (
            <Box display="flex">
              <IconButton component="label" sx={{ pointerEvents: "auto" }}>
                <FileUploadOutlined />
                <input
                  type="file"
                  hidden
                  multiple={true}
                  accept={field.accept ? field.accept.join(", ") : undefined}
                  name={fieldName + "_file"}
                  onChange={handleInputChange}
                />
              </IconButton>
            </Box>
          ),
        }}
        InputLabelProps={{
          required: field.required,
        }}
      />
      <Box>
        {field.value.map((file, idx) => {
          if (!("delete" in file)) {
            const name = "name" in file ? file.name : file.file.name;
            return (
              <Box
                display="grid"
                gridAutoFlow="column"
                justifyContent="start"
                alignItems="center"
                gap="8px"
                padding="4px"
                key={"id" in file ? file.id : file.file.name}
              >
                <ClickableIcon
                  icon={<Delete />}
                  size="small"
                  handleClick={() => {
                    handleDelete(idx);
                  }}
                />
                <Typography lineHeight={1}>{name}</Typography>
              </Box>
            );
          }
          return null;
        })}
      </Box>
    </Box>
  );
};

export default FormMultipleDynamicsFileInput;
